<script>
  import { onMount } from "svelte";
  import { Button, Icon, Badge } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import { getEventBadgeClass } from "../js/audit-utils";
  import AuditDecisionCardModal from "./AuditDecisionCardModal.svelte";
  import AuditOrderTimelineModal from "./AuditOrderTimelineModal.svelte";

  export let ucc = "";
  export let environment = "LIVE";

  let events = [];
  let loading = false;
  let error = null;

  let showDecisionModal = false;
  let selectedEvent = null;

  let showTimelineModal = false;
  let selectedOrderTag = null;

  $: if (ucc) {
    loadClientEvents(ucc, environment);
  } else {
    events = [];
    error = null;
  }

  async function loadClientEvents(clientUcc, env) {
    if (!clientUcc) return;
    loading = true;
    error = null;
    try {
      if (env === "SIMULATION") {
        const res = await fetch(`${BASE_URL}/api/v1/engine/sim/audit/timeline?ucc=${encodeURIComponent(clientUcc)}`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data = await res.json();
        const logs = Array.isArray(data) ? data : [];
        events = logs.map(l => ({
          event_type: l.action || 'SIM_ACTION',
          ucc: l.ucc,
          actor_id: l.actor_id,
          created_at: l.created_at,
          details: l.new_state ? (typeof l.new_state === 'string' ? l.new_state : JSON.stringify(l.new_state)) : '',
          symbol: 'SIM',
          side: 'ACTION',
        }));
      } else {
        const res = await fetch(`${BASE_URL}/api/v1/audit/events?ucc=${encodeURIComponent(clientUcc)}&environment=${env}&limit=30`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data = await res.json();
        events = Array.isArray(data.events) ? data.events : [];
      }
    } catch (err) {
      console.error("Error loading client audit timeline:", err);
      error = err.message || "Failed to load client audit timeline";
    } finally {
      loading = false;
    }
  }

  function formatTime(ts) {
    if (!ts) return "-";
    const d = new Date(ts);
    return isNaN(d.getTime()) ? ts : d.toLocaleTimeString('en-IN', { hour12: false });
  }

  function getBadgeClass(type) {
    return getEventBadgeClass(type);
  }
</script>

<div class="client-audit-wrapper">
  <div class="timeline-header">
    <div class="header-title">
      <span>📜 Execution Activity Timeline</span>
      <span class="ucc-pill">{ucc || 'No UCC Selected'}</span>
    </div>
    <Button small outline color="blue" onClick={() => loadClientEvents(ucc, environment)} disabled={loading}>
      <Icon f7="arrow_clockwise" size="12" /> &nbsp; Refresh
    </Button>
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner-small"></div>
      <span>Loading client execution history...</span>
    </div>
  {:else if error}
    <div class="error-state">
      <span>⚠️ {error}</span>
    </div>
  {:else if events.length === 0}
    <div class="empty-state">
      <span>No execution events recorded for client <strong>{ucc}</strong> today.</span>
    </div>
  {:else}
    <div class="client-feed">
      {#each events as evt}
        <div class="feed-item" on:click={() => { selectedEvent = evt; showDecisionModal = true; }}>
          <div class="feed-time">
            <code>{formatTime(evt.created_at || evt.timestamp)}</code>
          </div>
          <div class="feed-content">
            <div class="feed-top-row">
              <span class="event-pill {getBadgeClass(evt.event_type)}">{evt.event_type}</span>
              <span class="sym-tag">{evt.symbol || '-'}</span>
              {#if evt.side}
                <span class="side-tag {evt.side === 'BUY' ? 'text-green' : 'text-red'} font-bold">{evt.side}</span>
              {/if}
              {#if evt.fill_qty || evt.order_qty}
                <span class="qty-tag">{evt.fill_qty || evt.order_qty} Qty</span>
              {/if}
              {#if evt.fill_price || evt.order_price}
                <span class="price-tag">@ ₹{Number(evt.fill_price || evt.order_price).toFixed(2)}</span>
              {/if}
            </div>

            {#if evt.trigger_reason}
              <div class="feed-reason">
                {evt.trigger_reason}
              </div>
            {/if}

            <div class="feed-meta-row" on:click|stopPropagation>
              {#if evt.order_tag}
                <button class="link-btn" on:click={() => { selectedOrderTag = evt.order_tag; showTimelineModal = true; }}>
                  Order: <code>{evt.order_tag}</code> ➔
                </button>
              {/if}
              <button class="inspect-btn" on:click={() => { selectedEvent = evt; showDecisionModal = true; }}>
                Forensic Card 🔍
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<AuditDecisionCardModal
  show={showDecisionModal}
  event={selectedEvent}
  onClose={() => (showDecisionModal = false)}
/>

<AuditOrderTimelineModal
  show={showTimelineModal}
  orderTag={selectedOrderTag}
  environment={environment}
  onClose={() => (showTimelineModal = false)}
  onInspectEvent={(ev) => {
    showTimelineModal = false;
    selectedEvent = ev;
    showDecisionModal = true;
  }}
/>

<style>
  .client-audit-wrapper {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 12px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  .timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #334155;
    padding-bottom: 8px;
    margin-bottom: 12px;
  }
  .header-title {
    font-size: 13px;
    font-weight: 600;
    color: #e2e8f0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ucc-pill {
    background: #0f172a;
    color: #38bdf8;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    border: 1px solid #334155;
  }

  .loading-state, .empty-state, .error-state {
    text-align: center;
    padding: 24px;
    color: #94a3b8;
    font-size: 12px;
  }
  .spinner-small {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #334155;
    border-top-color: #38bdf8;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    vertical-align: middle;
    margin-right: 6px;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .client-feed {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 420px;
    overflow-y: auto;
  }
  .feed-item {
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 8px 12px;
    display: flex;
    gap: 12px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .feed-item:hover {
    background: #273549;
  }
  .feed-time code {
    color: #38bdf8;
    font-size: 11px;
  }
  .feed-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .feed-top-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }
  .event-pill {
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: bold;
  }
  .bg-green { background: #065f46; color: #34d399; }
  .bg-red { background: #7f1d1d; color: #f87171; }
  .bg-purple { background: #581c87; color: #c084fc; }
  .bg-blue { background: #1e3a8a; color: #60a5fa; }
  .bg-orange { background: #7c2d12; color: #fb923c; }
  .bg-slate { background: #334155; color: #94a3b8; }

  .sym-tag {
    font-weight: 600;
    color: #f1f5f9;
  }
  .feed-reason {
    font-size: 11px;
    color: #94a3b8;
  }
  .feed-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 2px;
    font-size: 11px;
  }
  .link-btn, .inspect-btn {
    background: transparent;
    border: none;
    color: #38bdf8;
    cursor: pointer;
    padding: 0;
    font-size: 11px;
  }
  .link-btn code {
    background: #1e293b;
    padding: 1px 4px;
    border-radius: 3px;
  }

  .text-green { color: #34d399 !important; }
  .text-red { color: #f87171 !important; }
  .font-bold { font-weight: bold; }
</style>
